import { useQuery } from '@tanstack/react-query';
import {
  AGING_BUCKETS,
  AGING_LABELS,
  type DashboardAging,
  type DashboardBankDebts,
  type DashboardMonth,
  type DashboardQuarter,
  type DashboardResponse,
  type DashboardSeries,
  DASHBOARD_QUARTERS,
  formatMoney,
  italianToday,
} from '@k-management/shared';
import { Link } from 'react-router';
import { useCan } from '../../auth/AuthProvider.js';
import { usePageTitle } from '../../components/layout/page-chrome.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { useSessionValue } from '../../lib/preferences.js';

const MONTHS = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'];
const FIRST_YEAR = 2024;

/** By month, from 1 January, or what is open day by day (2.35). */
type ChartView = 'mensile' | 'progressivo' | 'andamento';

const VIEW_LABELS: Record<ChartView, string> = {
  mensile: 'Mensile',
  progressivo: 'Progressivo',
  andamento: 'Andamento aperti',
};
/** Issued, received or both invoices (2.34). */
type Side = 'attive' | 'passive' | 'entrambe';

const SIDE_LABELS: Record<Side, string> = {
  attive: 'Attive',
  passive: 'Passive',
  entrambe: 'Entrambe',
};

const TITLES: Record<Side, Record<ChartView, string>> = {
  attive: {
    mensile: 'Fatturato per mese',
    progressivo: 'Fatturato progressivo',
    andamento: 'Crediti da incassare, andamento',
  },
  passive: {
    mensile: 'Acquisti per mese',
    progressivo: 'Acquisti progressivi',
    andamento: 'Debiti verso fornitori, andamento',
  },
  entrambe: {
    mensile: 'Fatturato e acquisti',
    progressivo: 'Fatturato e acquisti, progressivi',
    andamento: 'Crediti e debiti aperti, andamento',
  },
};

const TREND_EXPLAIN: Record<Side, string> = {
  attive:
    'Fatture emesse ancora da incassare in ogni giorno del periodo, senza IVA; più scura la parte scaduta.',
  passive:
    'Fatture ricevute e pro-forma ancora da pagare in ogni giorno del periodo, senza IVA; più scura la parte scaduta.',
  entrambe:
    'Crediti e debiti verso fornitori aperti in ogni giorno del periodo, senza IVA; la linea è la posizione netta.',
};

const EXPLAIN: Record<Side, string> = {
  attive: 'Imponibile delle fatture emesse meno le note di credito, per data di emissione.',
  passive:
    'Imponibile delle fatture ricevute meno le note di credito, per data del documento. Stipendi, F24 e spese senza fattura non ci sono ancora.',
  entrambe:
    'Fatturato e acquisti dello stesso anno; la linea è la differenza (fatturato − acquisti).',
};

/**
 * Report › Dashboard (2.33): the revenue of a year or a quarter against the
 * year before, monthly or progressive, and what is left to collect and to pay
 * at the end of the period by how late it is. An economic report, without VAT
 * (2.34): issued and received invoices, the debts to the banks apart. Totals
 * only, from the API.
 */
export function DashboardPage() {
  usePageTitle('Dashboard');
  const thisYear = Number(italianToday().slice(0, 4));
  const [year, setYear] = useSessionValue('report.dashboard.year', thisYear);
  const [quarter, setQuarter] = useSessionValue<DashboardQuarter | null>(
    'report.dashboard.quarter',
    null,
  );
  const [view, setView] = useSessionValue<ChartView>('report.dashboard.view', 'mensile');
  const [side, setSide] = useSessionValue<Side>('report.dashboard.side', 'attive');
  const [withoutIntercompany, setWithoutIntercompany] = useSessionValue(
    'report.dashboard.withoutIntercompany',
    true,
  );
  const seesInvoices = useCan('tesoreria.read');
  const dashboard = useQuery({
    queryKey: ['reports', 'dashboard', year, quarter, withoutIntercompany],
    queryFn: () =>
      apiRequest<DashboardResponse>(
        `/reports/dashboard?year=${year}${quarter ? `&quarter=${quarter}` : ''}${
          withoutIntercompany ? '' : '&intercompany=include'
        }`,
      ),
  });
  const years = Array.from({ length: thisYear - FIRST_YEAR + 1 }, (_, index) => thisYear - index);
  const data = dashboard.data;
  const progressive = view === 'progressivo';

  return (
    <section className="stack">
      <div className="row wrap dashboard-toolbar">
        <span className="dashboard-badge">Report economico · importi senza IVA</span>
        <span className="muted small">
          Fatture emesse e ricevute dai dati di Tesoreria; i debiti verso le banche a parte.
        </span>
      </div>
      <div className="row wrap dashboard-toolbar">
        <label className="field-inline">
          <span className="small">Anno</span>
          <select value={year} onChange={(event) => setYear(Number(event.target.value))}>
            {years.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
        </label>
        <div className="segmented-small" role="group" aria-label="Periodo">
          <button
            type="button"
            className={quarter === null ? 'selected' : ''}
            onClick={() => setQuarter(null)}
          >
            Anno
          </button>
          {DASHBOARD_QUARTERS.map((item) => (
            <button
              key={item}
              type="button"
              className={quarter === item ? 'selected' : ''}
              onClick={() => setQuarter(item)}
            >
              T{item}
            </button>
          ))}
        </div>
        {data && (
          <span className="muted small">
            Confronto con lo stesso periodo del {data.year - 1} · situazione al{' '}
            {formatDate(data.asOf)}
          </span>
        )}
      </div>

      <div className="row wrap dashboard-toolbar">
        <label className="checkbox">
          <input
            type="checkbox"
            checked={withoutIntercompany}
            onChange={(event) => setWithoutIntercompany(event.target.checked)}
          />
          Escludi intergruppo
        </label>
        {data && data.intercompany.count > 0 && (
          <span className="muted small">
            {data.intercompany.count === 1
              ? `${data.intercompany.included ? 'Compresa' : 'Esclusa'} 1 fattura intergruppo`
              : `${data.intercompany.included ? 'Comprese' : 'Escluse'} ${data.intercompany.count} fatture intergruppo`}
            : ricavi {formatMoney(data.intercompany.revenue)}, acquisti{' '}
            {formatMoney(data.intercompany.purchases)}. Crediti e debiti le comprendono sempre.
          </span>
        )}
      </div>

      {dashboard.isError && <p className="error">{dashboard.error.message}</p>}
      {!data && !dashboard.isError && <p className="muted">Caricamento…</p>}
      {data && (
        <>
          <div className="kpi-row">
            {side === 'entrambe' ? (
              <MarginKpi data={data} />
            ) : (
              <SeriesKpi
                label={side === 'attive' ? 'Fatturato' : 'Acquisti'}
                data={data}
                series={side === 'attive' ? data.revenue : data.purchases}
                lowerIsBetter={side === 'passive'}
              />
            )}
            <AgingKpi label="Crediti da incassare" aging={data.receivables} />
            <AgingKpi label="Debiti verso fornitori" aging={data.payables} />
            <div className="card kpi">
              <small className="muted">Posizione netta</small>
              <strong className={netClass(data)}>
                {signed(Number(data.receivables.total) - Number(data.payables.total))}
              </strong>
              <small className="muted">crediti − debiti verso fornitori</small>
            </div>
            {data.banks && <BanksKpi banks={data.banks} />}
          </div>

          <div className="dashboard-grid">
            <section className="card stack">
              <div className="row between wrap">
                <h2>{TITLES[side][view]}</h2>
                <div className="row wrap dashboard-toolbar">
                  <div className="segmented-small" role="group" aria-label="Fatture">
                    {(['attive', 'passive', 'entrambe'] as const).map((item) => (
                      <button
                        key={item}
                        type="button"
                        className={side === item ? 'selected' : ''}
                        onClick={() => setSide(item)}
                      >
                        {SIDE_LABELS[item]}
                      </button>
                    ))}
                  </div>
                  <div className="segmented-small" role="group" aria-label="Vista">
                    {(['mensile', 'progressivo', 'andamento'] as const).map((item) => (
                      <button
                        key={item}
                        type="button"
                        className={view === item ? 'selected' : ''}
                        onClick={() => setView(item)}
                      >
                        {VIEW_LABELS[item]}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              {view === 'andamento' ? (
                <TrendChart data={data} side={side} />
              ) : (
                <MonthsChart data={data} side={side} progressive={progressive} />
              )}
              <p className="muted small">
                {view === 'andamento' ? (
                  <>
                    {TREND_EXPLAIN[side]} Un punto{' '}
                    {data.trend.step === 'giorno' ? 'al giorno' : 'alla settimana'}; tratteggiato lo
                    stesso giorno del {data.year - 1}.
                  </>
                ) : (
                  <>
                    {EXPLAIN[side]}{' '}
                    {progressive
                      ? 'Somme da inizio anno, mese per mese.'
                      : 'Il mese in corso è più chiaro.'}
                  </>
                )}
              </p>
            </section>

            <section className="card stack">
              <h2>Crediti e debiti per scadenza</h2>
              <AgingTable data={data} seesInvoices={seesInvoices} />
              <AgingChart data={data} />
              <p className="muted small">
                Senza IVA: la parte dell’imponibile ancora da incassare o da pagare al{' '}
                {formatDate(data.asOf)}. Fatture ricevute e pro-forma per i debiti, fatture emesse
                per i crediti, note di credito comprese.
              </p>
            </section>
          </div>
        </>
      )}
    </section>
  );
}

function signed(value: number): string {
  return `${value > 0 ? '+' : ''}${formatMoney(value)}`;
}

function netClass(data: DashboardResponse): string {
  const net = Number(data.receivables.total) - Number(data.payables.total);
  return net > 0 ? 'text-ok' : net < 0 ? 'text-error' : '';
}

function periodLabel(data: DashboardResponse): string {
  return data.quarter ? `T${data.quarter} ${data.year}` : String(data.year);
}

function SeriesKpi({
  label,
  data,
  series,
  lowerIsBetter,
}: {
  label: string;
  data: DashboardResponse;
  series: DashboardSeries;
  lowerIsBetter: boolean;
}) {
  const total = Number(series.total);
  const previous = Number(series.previous);
  const change =
    previous !== 0 ? Math.round(((total - previous) / Math.abs(previous)) * 100) : null;
  const good = change !== null && (lowerIsBetter ? change <= 0 : change >= 0);
  return (
    <div className="card kpi">
      <small className="muted">
        {label} {periodLabel(data)}
      </small>
      <strong>{formatMoney(total)}</strong>
      <small className="muted">
        {change !== null && (
          <span className={good ? 'text-ok' : 'text-error'}>
            {change >= 0 ? '▲' : '▼'} {Math.abs(change)}%{' '}
          </span>
        )}
        sul {data.year - 1} ({formatMoney(previous)})
      </small>
    </div>
  );
}

function MarginKpi({ data }: { data: DashboardResponse }) {
  const margin = Number(data.revenue.total) - Number(data.purchases.total);
  return (
    <div className="card kpi">
      <small className="muted">Fatturato − acquisti {periodLabel(data)}</small>
      <strong className={margin > 0 ? 'text-ok' : margin < 0 ? 'text-error' : ''}>
        {signed(margin)}
      </strong>
      <small className="muted">
        fatturato {formatMoney(data.revenue.total)} · acquisti {formatMoney(data.purchases.total)}
      </small>
    </div>
  );
}

function AgingKpi({ label, aging }: { label: string; aging: DashboardAging }) {
  return (
    <div className="card kpi">
      <small className="muted">{label}</small>
      <strong>{formatMoney(aging.total)}</strong>
      <small className="muted">
        di cui scaduti{' '}
        <span className={Number(aging.overdue) > 0 ? 'text-error' : ''}>
          {formatMoney(aging.overdue)}
        </span>
      </small>
    </div>
  );
}

/** The debts to the banks today (2.34): financial debts, out of the position. */
function BanksKpi({ banks }: { banks: DashboardBankDebts }) {
  const invoices = banks.advancedInvoices === 1 ? 'fattura' : 'fatture';
  return (
    <div className="card kpi">
      <small className="muted">Debiti verso banche (a oggi)</small>
      <strong>{formatMoney(banks.total)}</strong>
      <small className="muted">
        anticipi fatture {formatMoney(banks.advances)}
        {banks.advancedInvoices > 0 && ` (${banks.advancedInvoices} ${invoices})`}
      </small>
      <small className="muted">
        mutui {formatMoney(banks.loans)}, entro 12 mesi {formatMoney(banks.loansNext12Months)}
      </small>
    </div>
  );
}

interface ChartSeries {
  key: string;
  label: string;
  className: string;
  /** Null where the month has nothing to show (not begun yet). */
  values: (number | null)[];
  titles: string[];
}

/**
 * The months of the period: the side chosen against the year before, or the
 * two sides of the same year with their difference; bars or, progressive,
 * lines from 1 January.
 */
function MonthsChart({
  data,
  side,
  progressive,
}: {
  data: DashboardResponse;
  side: Side;
  progressive: boolean;
}) {
  const year = data.year;
  const months = data.revenue.months;
  const at = (month: DashboardMonth, previous = false) =>
    Number(
      progressive
        ? previous
          ? month.previousProgressive
          : month.progressive
        : previous
          ? month.previous
          : month.amount,
    );
  const monthName = (month: DashboardMonth, of: number) => `${MONTHS[month.month - 1]} ${of}`;
  const suffix = (month: DashboardMonth) =>
    `${progressive ? ' da inizio anno' : ` · ${month.invoices} fatture`}${month.current ? ' (mese in corso)' : ''}`;
  const own = (series: DashboardSeries, name: string, className: string): ChartSeries => ({
    key: `${name}-${year}`,
    label: side === 'entrambe' ? `${name} ${year}` : String(year),
    className,
    values: series.months.map((month) => (month.future ? null : at(month))),
    titles: series.months.map(
      (month) => `${name} ${monthName(month, year)}: ${formatMoney(at(month))}${suffix(month)}`,
    ),
  });
  const before = (series: DashboardSeries, name: string, className: string): ChartSeries => ({
    key: `${name}-${year - 1}`,
    label: String(year - 1),
    className,
    values: series.months.map((month) => at(month, true)),
    titles: series.months.map(
      (month) =>
        `${name} ${monthName(month, year - 1)}: ${formatMoney(at(month, true))}${progressive ? ' da inizio anno' : ''}`,
    ),
  });
  const series: ChartSeries[] =
    side === 'attive'
      ? [before(data.revenue, 'Fatturato', 'previous'), own(data.revenue, 'Fatturato', 'current')]
      : side === 'passive'
        ? [
            before(data.purchases, 'Acquisti', 'purchase-previous'),
            own(data.purchases, 'Acquisti', 'purchase'),
          ]
        : [own(data.revenue, 'Fatturato', 'current'), own(data.purchases, 'Acquisti', 'purchase')];
  const difference =
    side === 'entrambe'
      ? series[0]!.values.map((value, index) => {
          const other = series[1]!.values[index];
          return value === null || other === null || other === undefined ? null : value - other;
        })
      : null;

  const width = CHART_WIDTH;
  const height = CHART_HEIGHT;
  const left = 64;
  const top = 14;
  const base = CHART_HEIGHT - 34;
  const slot = (width - left) / months.length;
  const values = [...series.flatMap((item) => item.values), ...(difference ?? [])].filter(
    (value): value is number => value !== null,
  );
  const ticks = niceTicks(Math.min(...values, 0), Math.max(...values, 1));
  const min = ticks[0]!;
  const max = ticks[ticks.length - 1]!;
  const y = (value: number) => base - ((value - min) / (max - min)) * (base - top);
  const center = (index: number) => left + index * slot + slot / 2;
  const bar = Math.min(34, (slot - 14) / 2);
  const line = (points: (number | null)[]) =>
    points
      .map((value, index) => (value === null ? null : `${center(index)},${y(value)}`))
      .filter(Boolean)
      .join(' ');

  return (
    <div className="dashboard-chart">
      <div className="budget-legend small">
        {series.map((item) => (
          <span key={item.key}>
            <i className={`swatch ${item.className}`} /> {item.label}
          </span>
        ))}
        {difference && (
          <span>
            <i className="swatch difference" /> Differenza
          </span>
        )}
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Andamento per mese">
        {ticks.map((value) => (
          <g key={value}>
            <line x1={left} x2={width} y1={y(value)} y2={y(value)} className="grid" />
            <text x={left - 6} y={y(value) + 4} textAnchor="end" className="tick">
              {short(value)}
            </text>
          </g>
        ))}
        {!progressive &&
          months.map((month, index) => (
            <g key={month.month}>
              {series.map((item, layer) => {
                const value = item.values[index];
                if (value === null || value === undefined) return null;
                const ofYear = side === 'entrambe' || layer === 1;
                return (
                  <Bar
                    key={item.key}
                    x={center(index) - bar - 2 + layer * (bar + 4)}
                    width={bar}
                    from={y(0)}
                    to={y(value)}
                    className={`${item.className}${ofYear && month.current ? ' partial' : ''}`}
                    title={item.titles[index]!}
                  />
                );
              })}
            </g>
          ))}
        {progressive &&
          series.map((item) => (
            <g key={item.key}>
              <polyline className={`line ${item.className}`} points={line(item.values)} />
              {item.values.map((value, index) =>
                value === null ? null : (
                  <circle
                    key={index}
                    cx={center(index)}
                    cy={y(value)}
                    r={3.5}
                    className={`dot ${item.className}`}
                  >
                    <title>{item.titles[index]}</title>
                  </circle>
                ),
              )}
            </g>
          ))}
        {difference && (
          <g>
            <polyline className="line difference" points={line(difference)} />
            {difference.map((value, index) =>
              value === null ? null : (
                <circle
                  key={index}
                  cx={center(index)}
                  cy={y(value)}
                  r={3.5}
                  className="dot difference"
                >
                  <title>{`Differenza ${monthName(months[index]!, year)}: ${formatMoney(value)}`}</title>
                </circle>
              ),
            )}
          </g>
        )}
        {months.map((month, index) => (
          <text
            key={`m${month.month}`}
            x={center(index)}
            y={base + 18}
            textAnchor="middle"
            className="tick"
          >
            {MONTHS[month.month - 1]}
          </text>
        ))}
      </svg>
    </div>
  );
}

function Bar({
  x,
  width,
  from,
  to,
  className,
  title,
}: {
  x: number;
  width: number;
  from: number;
  to: number;
  className: string;
  title: string;
}) {
  return (
    <rect
      x={x}
      y={Math.min(from, to)}
      width={width}
      height={Math.abs(from - to)}
      rx={3}
      className={className}
    >
      <title>{title}</title>
    </rect>
  );
}

function AgingTable({ data, seesInvoices }: { data: DashboardResponse; seesInvoices: boolean }) {
  const head = (label: string, to: string) => (seesInvoices ? <Link to={to}>{label}</Link> : label);
  return (
    <div className="table-scroll">
      <table className="lines">
        <thead>
          <tr>
            <th>Scadenza</th>
            <th className="right">{head('Crediti', '/tesoreria/fatture-attive')}</th>
            <th className="right">{head('Debiti', '/tesoreria/fatture-passive')}</th>
          </tr>
        </thead>
        <tbody>
          {AGING_BUCKETS.map((bucket) => (
            <tr key={bucket}>
              <td>{AGING_LABELS[bucket]}</td>
              <td className="right">{formatMoney(data.receivables.buckets[bucket])}</td>
              <td className="right">{formatMoney(data.payables.buckets[bucket])}</td>
            </tr>
          ))}
          <tr className="dashboard-total">
            <td>Totale</td>
            <td className="right">{formatMoney(data.receivables.total)}</td>
            <td className="right">{formatMoney(data.payables.total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/** Two horizontal bars per bucket: receivables above, payables below. */
function AgingChart({ data }: { data: DashboardResponse }) {
  const width = 640;
  const left = 96;
  const row = 32;
  const height = AGING_BUCKETS.length * row + 4;
  const values = AGING_BUCKETS.flatMap((bucket) => [
    Number(data.receivables.buckets[bucket]),
    Number(data.payables.buckets[bucket]),
  ]);
  const max = Math.max(...values.map(Math.abs), 1);
  const length = (value: number) => (Math.max(value, 0) / max) * (width - left - 8);
  return (
    <div className="dashboard-chart">
      <div className="budget-legend small">
        <span>
          <i className="swatch receivable" /> Crediti
        </span>
        <span>
          <i className="swatch payable" /> Debiti
        </span>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Crediti e debiti per scadenza">
        {AGING_BUCKETS.map((bucket, index) => {
          const top = index * row + 4;
          const receivable = Number(data.receivables.buckets[bucket]);
          const payable = Number(data.payables.buckets[bucket]);
          return (
            <g key={bucket}>
              <text x={left - 8} y={top + 14} textAnchor="end" className="tick">
                {AGING_LABELS[bucket].replace('Scaduti da ', '')}
              </text>
              <rect
                x={left}
                y={top}
                width={length(receivable)}
                height={11}
                rx={2}
                className="receivable"
              >
                <title>{`Crediti, ${AGING_LABELS[bucket]}: ${formatMoney(receivable)}`}</title>
              </rect>
              <rect
                x={left}
                y={top + 12}
                width={length(payable)}
                height={11}
                rx={2}
                className="payable"
              >
                <title>{`Debiti, ${AGING_LABELS[bucket]}: ${formatMoney(payable)}`}</title>
              </rect>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/** Size of the charts: wide enough that their text reads like the page (2.35). */
const CHART_WIDTH = 960;
const CHART_HEIGHT = 300;

/** Round ticks from below `min` to above `max`, zero included: 1, 2, 2.5 or 5 times a power of ten. */
function niceTicks(min: number, max: number, count = 4): number[] {
  const range = Math.max(max - min, 1);
  const rough = range / count;
  const power = 10 ** Math.floor(Math.log10(rough));
  const step = ([1, 2, 2.5, 5, 10].find((item) => item * power >= rough) ?? 10) * power;
  const ticks: number[] = [];
  for (let value = Math.floor(min / step) * step; value < max + step / 2; value += step) {
    ticks.push(Math.round(value));
  }
  return ticks.length > 1 ? ticks : [0, step];
}

/**
 * What is open day by day (2.35): the side chosen with its overdue part and
 * the same days of the year before; both sides with the net position.
 */
function TrendChart({ data, side }: { data: DashboardResponse; side: Side }) {
  const { points, previous, step } = data.trend;
  if (points.length === 0) return <p className="muted">Il periodo non è ancora iniziato.</p>;
  type Line = { key: string; label: string; className: string; values: number[]; title: string };
  const of = (list: typeof points, field: keyof (typeof points)[number]) =>
    list.map((point) => Number(point[field]));
  const lines: Line[] =
    side === 'entrambe'
      ? [
          {
            key: 'crediti',
            label: 'Crediti aperti',
            className: 'receivable',
            values: of(points, 'receivables'),
            title: 'Crediti',
          },
          {
            key: 'debiti',
            label: 'Debiti verso fornitori',
            className: 'payable',
            values: of(points, 'payables'),
            title: 'Debiti',
          },
          {
            key: 'netto',
            label: 'Posizione netta',
            className: 'difference',
            values: points.map((point) => Number(point.receivables) - Number(point.payables)),
            title: 'Posizione netta',
          },
        ]
      : (() => {
          const total = side === 'attive' ? 'receivables' : 'payables';
          const overdue = side === 'attive' ? 'receivablesOverdue' : 'payablesOverdue';
          const name = side === 'attive' ? 'Crediti' : 'Debiti';
          const tone = side === 'attive' ? 'receivable' : 'payable';
          return [
            {
              key: 'prima',
              label: `Aperti ${data.year - 1}`,
              className: `${tone} before`,
              values: of(previous, total),
              title: `${name} aperti ${data.year - 1}`,
            },
            {
              key: 'aperti',
              label: `Aperti ${data.year}`,
              className: tone,
              values: of(points, total),
              title: `${name} aperti`,
            },
            {
              key: 'scaduti',
              label: 'di cui scaduti',
              className: `${tone} overdue`,
              values: of(points, overdue),
              title: `${name} scaduti`,
            },
          ];
        })();

  const width = CHART_WIDTH;
  const height = CHART_HEIGHT;
  const left = 64;
  const top = 14;
  const base = CHART_HEIGHT - 34;
  const all = lines.flatMap((line) => line.values);
  const ticks = niceTicks(Math.min(...all, 0), Math.max(...all, 1));
  const min = ticks[0]!;
  const max = ticks[ticks.length - 1]!;
  const y = (value: number) => base - ((value - min) / (max - min)) * (base - top);
  const x = (index: number) =>
    left + (points.length === 1 ? 0 : (index / (points.length - 1)) * (width - left - 12));
  const path = (values: number[]) =>
    values.map((value, index) => `${x(index)},${y(value)}`).join(' ');
  // A label at each new month (a year) or each Monday (a quarter).
  const labels = points
    .map((point, index) => ({ point, index }))
    .filter(({ point, index }) =>
      step === 'settimana'
        ? index === 0 || point.date.slice(5, 7) !== points[index - 1]!.date.slice(5, 7)
        : index === 0 || new Date(`${point.date}T00:00:00Z`).getUTCDay() === 1,
    );
  const last = points.length - 1;

  return (
    <div className="dashboard-chart">
      <div className="budget-legend small">
        {lines.map((line) => (
          <span key={line.key}>
            <i className={`swatch ${line.className}`} /> {line.label}
          </span>
        ))}
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Andamento degli aperti">
        {ticks.map((value) => (
          <g key={value}>
            <line x1={left} x2={width} y1={y(value)} y2={y(value)} className="grid" />
            <text x={left - 6} y={y(value) + 4} textAnchor="end" className="tick">
              {short(value)}
            </text>
          </g>
        ))}
        {lines.map((line) => (
          <polyline
            key={line.key}
            className={`line ${line.className}`}
            points={path(line.values)}
          />
        ))}
        {points.map((point, index) => (
          <rect
            key={point.date}
            x={x(index) - (width - left) / points.length / 2}
            y={top}
            width={Math.max((width - left) / points.length, 2)}
            height={base - top}
            className="hover-band"
          >
            <title>
              {[
                formatDate(point.date),
                ...lines.map((line) => `${line.title}: ${formatMoney(line.values[index]!)}`),
              ].join('\n')}
            </title>
          </rect>
        ))}
        <circle
          cx={x(last)}
          cy={y(lines[side === 'entrambe' ? 2 : 1]!.values[last]!)}
          r={4}
          className={`dot ${lines[side === 'entrambe' ? 2 : 1]!.className}`}
        />
        <text
          x={x(last)}
          y={y(lines[side === 'entrambe' ? 2 : 1]!.values[last]!) - 10}
          textAnchor="end"
          className="value"
        >
          {short(lines[side === 'entrambe' ? 2 : 1]!.values[last]!)}
        </text>
        {labels.map(({ point, index }) => (
          <text
            key={`l${point.date}`}
            x={x(index)}
            y={base + 20}
            textAnchor="middle"
            className="tick"
          >
            {step === 'settimana'
              ? MONTHS[Number(point.date.slice(5, 7)) - 1]
              : `${point.date.slice(8, 10)}/${point.date.slice(5, 7)}`}
          </text>
        ))}
      </svg>
    </div>
  );
}

function short(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000)
    return `${(value / 1_000_000).toLocaleString('it-IT', { maximumFractionDigits: 1 })}M`;
  if (abs >= 1_000) return `${Math.round(value / 1_000)}k`;
  return String(Math.round(value));
}
